// FormElements.js
import React, { useEffect } from 'react';
import './FormEditorStyles.css';
import { applyTimeZone, fetchAPI, getSubdomain, postAPI } from '../../utils/utils';
import { getStripeCommissionInfoSync } from '../../utils/stripeCommission';
import { formatMoney } from '../../utils/money';
import { useState } from 'react';

const sanitizeDecimalInput = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  const stringValue = value.toString();
  const replaced = stringValue.replace(/,/g, '.');
  const allowedCharacters = replaced.replace(/[^0-9.]/g, '');
  const [integerPart, ...decimalParts] = allowedCharacters.split('.');

  if (decimalParts.length === 0) {
    return integerPart;
  }

  return `${integerPart}.${decimalParts.join('')}`;
};

// Stored with a decimal point ("11.9"), shown with a decimal comma ("11,9")
const decimalValueToInput = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  return value.toString().replace('.', ',');
};

const sanitizeLimitInput = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsedValue = parseInt(value, 10);

  if (Number.isNaN(parsedValue)) {
    return null;
  }

  return Math.max(0, parsedValue);
};

const COLLA = getSubdomain();

const EventSelect = ({ value, onChange, events, selectedEvent }) => {
  const now = new Date();

  const formatEventLabel = (ev) => {
    const title = ev.title || `Esdeveniment ${ev.id}`;
    if (ev['data-esperada-inici']) {
      const date = applyTimeZone(ev['data-esperada-inici']).format('DD/MM/YY HH:mm');
      return `${title} (${date})`;
    }
    return title;
  };

  // Filter events: only show events where end date >= now OR is the currently selected event
  const relevantEvents = events.filter((ev) => {
    if (String(ev.id) === String(value)) return true; // Always show the currently selected event
    const endDate = new Date(ev['data-esperada-fi'] || ev['data-esperada-inici'] || 0);
    return endDate >= now;
  });

  // The current form's event (selectedEvent) - should appear first/highlighted
  const currentFormEvent = relevantEvents.find((ev) => String(ev.id) === String(selectedEvent));

  // Future events (sorted chronologically, soonest first)
  const futureEvents = relevantEvents
    .filter((ev) => String(ev.id) !== String(selectedEvent))
    .sort((a, b) => {
      const dateA = new Date(a['data-esperada-inici'] || 0);
      const dateB = new Date(b['data-esperada-inici'] || 0);
      return dateA - dateB;
    });

  return (
    <select value={value || ''} onChange={onChange} style={{ padding: '8px', fontSize: '14px' }}>
      <option value="">Selecciona un esdeveniment</option>
      {currentFormEvent ? (
        <optgroup label="Esdeveniment actual">
          <option key={currentFormEvent.id} value={currentFormEvent.id}>
            {formatEventLabel(currentFormEvent)}
          </option>
        </optgroup>
      ) : null}
      {futureEvents.length > 0 ? (
        <optgroup label="Propers esdeveniments">
          {futureEvents.map((ev) => (
            <option key={ev.id} value={ev.id}>
              {formatEventLabel(ev)}
            </option>
          ))}
        </optgroup>
      ) : null}
    </select>
  );
};

const AssistanceQuestion = ({ element, updateElement, removeElement, errors, events = [], selectedEvent }) => {
  const { content } = element;

  const updateQuestion = (e) => {
    updateElement({ ...element, content: { ...content, question: e.target.value } });
  };

  const updateEvent = (e) => {
    updateElement({ ...element, content: { ...content, eventId: e.target.value } });
  };

  return (
    <>
      <input
        type="text"
        value={content.question}
        onChange={updateQuestion}
        placeholder="Exemple: Vens a l'actuació?"
        className={(errors.question && !errors.question.isWarning ? 'error' : '') + ' question'}
      />
      {errors.question && (
        <p className={errors.question.isWarning ? "warning-text" : "error-text"}>
          {errors.question.message || errors.question}
        </p>
      )}

      <div className="option-container" style={{ paddingLeft: 0, marginTop: 10 }}>
        <label style={{ fontWeight: 500, marginBottom: 4, display: 'block' }}>Lliga la resposta amb l'assistència de</label>
        <EventSelect
          value={content.eventId}
          onChange={updateEvent}
          events={events}
          selectedEvent={selectedEvent}
        />
        {errors.eventId && (
          <p className={errors.eventId.isWarning ? 'warning-text' : 'error-text'}>
            {errors.eventId.message || errors.eventId}
          </p>
        )}
      </div>

      <div className="options-container">
        <div className="option-container">
          <input disabled type="radio" name={`assistance_${element.id}`} checked />
          <label>
            <input
              type="text"
              value={content.yesLabel}
              onChange={(e) => updateElement({ ...element, content: { ...content, yesLabel: e.target.value } })}
              placeholder="Sí"
              className={errors.yesLabel && !errors.yesLabel.isWarning ? 'option-input error' : 'option-input'}
            />
            {errors.yesLabel && (
              <p className={errors.yesLabel.isWarning ? 'warning-text' : 'error-text'}>
                {errors.yesLabel.message || errors.yesLabel}
              </p>
            )}
          </label>
        </div>
        <div className="option-container">
          <input disabled type="radio" name={`assistance_${element.id}`} />
          <label>
            <input
              type="text"
              value={content.noLabel}
              onChange={(e) => updateElement({ ...element, content: { ...content, noLabel: e.target.value } })}
              placeholder="No"
              className={errors.noLabel && !errors.noLabel.isWarning ? 'option-input error' : 'option-input'}
            />
            {errors.noLabel && (
              <p className={errors.noLabel.isWarning ? 'warning-text' : 'error-text'}>
                {errors.noLabel.message || errors.noLabel}
              </p>
            )}
          </label>
        </div>
      </div>

    </>
  );
};

const MultipleChoiceQuestion = ({ element, updateElement, removeElement, errors }) => {
  const { content } = element;

  const updateQuestion = (e) => {
    updateElement({ ...element, content: { ...content, question: e.target.value } });
  };

  const updateOption = (index, value) => {
    const newOptions = content.options.map((option, i) =>
      i === index ? value : option,
    );
    updateElement({ ...element, content: { ...content, options: newOptions } });
  };

  const addOption = () => {
    updateElement({
      ...element,
      content: { ...content, options: [...content.options, ''] },
    });
  };

  const removeOption = (index) => {
    const newOptions = content.options.filter((_, i) => i !== index);
    updateElement({ ...element, content: { ...content, options: newOptions } });
  };

  return (
    <>
      <input
        type="text"
        value={content.question}
        onChange={updateQuestion}
        placeholder="Resposta d'una única opció"
        className={(errors.question && !errors.question.isWarning ? 'error' : '') + ' question'}
      />
      {errors.question && (
        <p className={errors.question.isWarning ? "warning-text" : "error-text"}>
          {errors.question.message || errors.question}
        </p>
      )}

      <div className="options-container">
        {content.options.map((option, index) => (
        <div key={index} className="option-container">
            <input disabled type="radio" name={`question_${element.id}`} id={`option_${index}`} />
            <label htmlFor={`option_${index}`}>
            <input
                type="text"
                value={option}
                onChange={(e) => updateOption(index, e.target.value)}
                placeholder={`Opció ${index + 1}`}
                className={errors[`option_${index}`] && !errors[`option_${index}`].isWarning ? "option-input error" : "option-input"}
            />
            { errors[`option_${index}`] && (
              <p className={errors[`option_${index}`].isWarning ? "warning-text" : "error-text"}>
                {errors[`option_${index}`].message || errors[`option_${index}`]}
              </p>
            )}
            </label>
            {/* <button className='remove-option' onClick={() => removeOption(index)}>🗑️</button> */}
        </div>
        ))}
        </div>
        <div style={{ marginTop: 20 }}>
          <button onClick={addOption}>Afegeix una altra opció</button>
        </div>
    </>
  );
};

const CheckboxQuestion = ({ element, updateElement, removeElement, errors }) => {
    const { content } = element;
  
    const updateQuestion = (e) => {
      updateElement({ ...element, content: { ...content, question: e.target.value } });
    };
  
    const updateOption = (index, value) => {
      const newOptions = content.options.map((option, i) =>
        i === index ? value : option,
      );
      updateElement({ ...element, content: { ...content, options: newOptions } });
    };
  
    const addOption = () => {
      updateElement({
        ...element,
        content: { ...content, options: [...content.options, ''] },
      });
    };
  
    const removeOption = (index) => {
      const newOptions = content.options.filter((_, i) => i !== index);
      updateElement({ ...element, content: { ...content, options: newOptions } });
    };
  
    return (
    <>
        <input
          type="text"
          value={content.question}
          onChange={updateQuestion}
          placeholder="Resposta amb múltiples opcions"
          className={(errors.question && !errors.question.isWarning ? 'error' : '') + ' question'}
        />
        {errors.question && (
          <p className={errors.question.isWarning ? "warning-text" : "error-text"}>
            {errors.question.message || errors.question}
          </p>
        )}
      <div className="options-container">
        {content.options.map((option, index) => (
            <div key={index} className="option-container">
                <input disabled type="checkbox" id={`option_${index}`} />
                <label htmlFor={`option_${index}`}>
                  <input
                      type="text"
                      value={option}
                      onChange={(e) => updateOption(index, e.target.value)}
                      placeholder={`Opció ${index + 1}`}
                      className={errors[`option_${index}`] && !errors[`option_${index}`].isWarning ? "option-input error" : "option-input"}
                  />
                  { errors[`option_${index}`] && (
                    <p className={errors[`option_${index}`].isWarning ? "warning-text" : "error-text"}>
                      {errors[`option_${index}`].message || errors[`option_${index}`]}
                    </p>
                  )}
                </label>
                {/* <button className='remove-option' onClick={() => removeOption(index)}>🗑️</button> */}
            </div>
        ))}
        </div>
        <div style={{ marginTop: 20 }}>
          <button onClick={addOption}>Afegeix una altra opció</button>
        </div>
      </>
    );
  };
 
const ShortAnswerQuestion = ({ element, updateElement, removeElement, errors }) => {
  const { content } = element;

  const updateQuestion = (e) => {
    updateElement({ ...element, content: { ...content, question: e.target.value } });
  };

  return (
    <>
      <input
        type="text"
        value={content.question}
        onChange={updateQuestion}
        placeholder="Pregunta de resposta curta"
        className={(errors.question && !errors.question.isWarning ? 'error' : '') + ' question'}
      />
      {errors.question && (
        <p className={errors.question.isWarning ? "warning-text" : "error-text"}>
          {errors.question.message || errors.question}
        </p>
      )}
    </>
  );
};

const ParagraphQuestion = ({ element, updateElement, removeElement, errors }) => {
  const { content } = element;

  const updateQuestion = (e) => {
    updateElement({ ...element, content: { ...content, question: e.target.value } });
  };

  return (
    <>
      <input
        type="text"
        value={content.question}
        onChange={updateQuestion}
        placeholder="Pregunta de resposta llarga"
        className={(errors.question && !errors.question.isWarning ? 'error' : '') + ' question'}
      />
      {errors.question && (
        <p className={errors.question.isWarning ? "warning-text" : "error-text"}>
          {errors.question.message || errors.question}
        </p>
      )}
    </>
  );
};

const ImageUploadQuestion = ({ element, updateElement, removeElement, errors }) => {
  const { content } = element;

  const updateQuestion = (e) => {
    updateElement({ ...element, content: { ...content, question: e.target.value } });
  };

  const updateSkipWhenNoPay = (checked) => {
    updateElement({ ...element, skipWhenNoPay: checked ? false : true });
  };

  return (
    <>
      {
        element?.isComprovant && (
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={element.skipWhenNoPay === false}
                onChange={(e) => updateSkipWhenNoPay(e.target.checked)}
              />
              <span style={{ fontSize: 14 }}>Demana sempre comprovant, encara que no hagin de pagar res</span>
            </label>
            <p style={{ fontSize: 12, marginTop: 5, fontStyle: 'italic', color: '#666' }}>
              {element.skipWhenNoPay === false
                ? "Demanarà el comprovant a tothom, independentment de si ha de pagar o no."
                : "Només demanarà el comprovant si l'usuari ha de pagar més de 0€."
              }
            </p>
          </div>
        )
      }
      <input
        type="text"
        value={content?.question || ""}
        onChange={updateQuestion}
        placeholder={element?.isComprovant ? "Proposta de títol: Penja el comprovant del pagament" : "Títol de la imatge a pujar"}
        className={(errors.question && !errors.question.isWarning ? 'error' : '') + ' question'}
      />
      <input
        type="file"
        disabled
        style={{
          marginTop: 10,
        }}
      />
      {errors.question && (
        <p className={errors.question.isWarning ? "warning-text" : "error-text"}>
          {errors.question.message || errors.question}
        </p>
      )}
    </>
  );
};

const TextElement = ({ element, updateElement, removeElement, errors }) => {
  const { content } = element;

  const updateText = (e) => {
    updateElement({ ...element, content: e.target.value });
  };

  return (
    <>
      <textarea
        value={content}
        onChange={updateText}
        placeholder="Afegeix text sense pregunta"
        className={(errors.text && !errors.text.isWarning ? 'error' : '') + ' question'}
      ></textarea>
      {errors.text && (
        <p className={errors.text.isWarning ? "warning-text" : "error-text"}>
          {errors.text.message || errors.text}
        </p>
      )}
      <p style={{ fontSize: 10 }}>Pots utilitzar: <span style={{ color: 'darkblue' }}>{['{nom}', '{cognom}', '{sobrenom}'].join(', ')}</span> als textos. També pots posar <b>*negretes*</b> i <i>_cursives_</i> amb aquest format.</p>
    </>
  );
};

const ImageElement = ({ selectedEvent, element, updateElement, removeElement, errors }) => {
  const { content } = element;
  const [imageURL, setImageURL] = useState(null);

  useEffect(() => {
    if (!selectedEvent) return;

    fetchAPI(`/form_image_url/${selectedEvent}/${element.id}`, ({ elementId, url }) => {
      fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error('Error fetching image from S3');
          return res.text();
        })
        .then((data) => {
          setImageURL(data);
        })
        .catch((err) => {
          // Not found
        })
    }, false, false)
  }, [
    selectedEvent,
    element.id,
  ])

  const uploadFormImage = async (base64, formId, elementId, callback) => {
    postAPI('/upload_form_image', {
      base64,
      formId,
      elementId,
    }, ({ url }) => {
      callback(url);
    })
  }

  return (
    <>
      {
        imageURL &&
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            margin: 10,
            marginBottom: 30,
          }}
        >
          <img
            src={imageURL}
            style={{
              maxWidth: '50%',
              borderRadius: 10,
            }}
          />
        </div>
      }

      <label
        htmlFor={`image_${element.id}`}
        style={{
          fontWeight: 'bold',
        }}
      >
        {
          imageURL ?
          'Canvia la imatge:' :
          'Afegeix una imatge:'
        }
      </label>

      <input
        type="file"
        id={`image_${element.id}`}
        onChange={(e) => {
          const file = e.target.files[0];
          const reader = new FileReader();

          reader.onload = () => {
            uploadFormImage(reader.result, selectedEvent, element.id, () => {
              setImageURL(reader.result);
            })
          };

          reader.readAsDataURL(file);
        }}
      />

    </>
  );
};


const TicketQuestion = ({ element, updateElement, removeElement, errors }) => {
  const { content } = element;

  const removeTiquet = (data) => {
    const isRemoved = removeElement(data)
    if (!isRemoved) return;

    postAPI('/remove_tiquet', { id: element.id })
  }

  const updateQuestion = (e) => {
    updateElement({ ...element, content: { ...content, question: e.target.value } });
  };

  const updateOption = (index, value) => {
    const newOptions = content.options.map((option, i) =>
      i === index ? value : option,
    );
    updateElement({ ...element, content: { ...content, options: newOptions } });
  };

  return (
    <>
      <input
        type="text"
        value={content.question}
        onChange={updateQuestion}
        placeholder="Nom del pagament"
        className={(errors.question && !errors.question.isWarning ? 'error' : '') + ' question'}
      />
      {errors.question && (
        <p className={errors.question.isWarning ? "warning-text" : "error-text"}>
          {errors.question.message || errors.question}
        </p>
      )}
      <p style={{ fontSize: 11, color: '#777' }}>Quan la gent respongui la primera opció, s'afegiran a la llista de "demanats" per aquest pagament.</p>
      <div className="options-container">
        {content.options.map((option, index) => (
            <div key={index} className="option-container">
                <input
                  disabled
                  checked={index === 0}
                  type="checkbox"
                  id={`option_${index}`}
                />
                <label htmlFor={`option_${index}`}>
                  <input
                      type="text"
                      value={option}
                      onChange={(e) => updateOption(index, e.target.value)}
                      placeholder={index === 0 ? 'Opció 1 (demanats)' : `Opció ${index + 1}`}
                      className="option-input"
                  />
                  { errors[`option_${index}`] && (
                    <p className={errors[`option_${index}`].isWarning ? "warning-text" : "error-text"}>
                      {errors[`option_${index}`].message || errors[`option_${index}`]}
                    </p>
                  )}
                </label>
                {
                  index === 0 &&
                  <>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={decimalValueToInput(element.price)}
                      onChange={(e) => {
                        const sanitizedPrice = sanitizeDecimalInput(e.target.value);
                        updateElement({ ...element, price: sanitizedPrice });
                      }}
                      placeholder="Preu"
                      className="option-input"
                      style={{ width: 55, flexGrow: 0, marginLeft: 10 }}
                    />
                    €
                    <input
                      type="number"
                      value={element.limit ?? ''}
                      onWheel={(event) => event.currentTarget.blur()}
                      onChange={(e) => {
                        updateElement({ ...element, limit: sanitizeLimitInput(e.target.value) })
                      }}
                      placeholder="Límit"
                      className="option-input"
                      style={{ width: 55, flexGrow: 0, marginLeft: 10 }}
                      min={0}
                    />
                  </>
                }
            </div>
        ))}
        </div>
    </>
  );
};

const TicketV2Question = ({ element, updateElement, removeElement, errors }) => {
  const { content } = element;

  const removeTiquet = (data) => {
    const isRemoved = removeElement(data)
    if (!isRemoved) return;

    postAPI('/remove_tiquet', { id: element.id })
  }

  const updateQuestion = (e) => {
    updateElement({ ...element, content: { ...content, question: e.target.value } });
  };

  const updateOption = (index, field, value) => {
    const newOptions = content.options.map((option, i) =>
      i === index ? { ...option, [field]: value } : option
    );
    updateElement({ ...element, content: { ...content, options: newOptions } });
  };

  const addOption = () => {
    const newOptions = [...content.options, { text: '', price: 0, limit: null }];
    updateElement({ ...element, content: { ...content, options: newOptions } });
  };

  const removeOption = (index) => {
    if (content.options.length > 1) {
      const newOptions = content.options.filter((_, i) => i !== index);
      updateElement({ ...element, content: { ...content, options: newOptions } });
    }
  };

  return (
    <>
      <input
        type="text"
        value={content.question}
        onChange={updateQuestion}
        placeholder="Nom del pagament (amb quantitats)"
        className={(errors.question && !errors.question.isWarning ? 'error' : '') + ' question'}
      />
      {errors.question && (
        <p className={errors.question.isWarning ? "warning-text" : "error-text"}>
          {errors.question.message || errors.question}
        </p>
      )}
      <p style={{ fontSize: 11, color: '#777' }}>Quan la gent respongui una opció, s'afegiran a la llista de "demanats" per aquest pagament.</p>
      <div className="options-container">
        {content.options.map((option, index) => (
          <div key={index} className="option-container">
            {errors[`option_${index}`] && (
              <p className={errors[`option_${index}`].isWarning ? "warning-text" : "error-text"} style={{ width: 100 }}>
                {errors[`option_${index}`].message || errors[`option_${index}`]}
              </p>
            )}
            <input
              type="text"
              value={option.text}
              onChange={(e) => updateOption(index, 'text', e.target.value)}
              placeholder={`Opció ${index + 1}`}
              className="option-input"
            />
            <input
              type="text"
              inputMode="decimal"
              value={decimalValueToInput(option.price)}
              onChange={(e) => updateOption(index, 'price', sanitizeDecimalInput(e.target.value))}
              placeholder="Preu"
              className="option-input"
              style={{ width: 55, flexGrow: 0, marginLeft: 10 }}
            />
            €
            <input
              type="number"
              value={option.limit ?? ''}
              onWheel={(event) => event.currentTarget.blur()}
              onChange={(e) => updateOption(index, 'limit', sanitizeLimitInput(e.target.value))}
              placeholder="Límit"
              className="option-input"
              style={{ width: 55, flexGrow: 0, marginLeft: 10 }}
              min={0}
            />
            {/* <button onClick={() => removeOption(index)} style={{ marginLeft: 10 }}>🗑️</button> */}
          </div>
        ))}
        <button onClick={addOption} style={{ marginTop: 10 }}>+ Afegir opció</button>
      </div>
    </>
  );
};

const TicketV2SingleQuestion = ({ element, updateElement, removeElement, errors }) => {
  const { content } = element;

  const removeTiquet = (data) => {
    const isRemoved = removeElement(data)
    if (!isRemoved) return;

    postAPI('/remove_tiquet', { id: element.id })
  }

  const updateQuestion = (e) => {
    updateElement({ ...element, content: { ...content, question: e.target.value } });
  };

  const updateOption = (index, field, value) => {
    const newOptions = content.options.map((option, i) =>
      i === index ? { ...option, [field]: value } : option
    );
    updateElement({ ...element, content: { ...content, options: newOptions } });
  };

  const addOption = () => {
    const newOptions = [...content.options, { text: '', price: 0, limit: null }];
    updateElement({ ...element, content: { ...content, options: newOptions } });
  };

  const hideOption = (index) => {
    const newOptions = content.options.map((option, i) =>
      i === index ? { ...option, hidden: true } : option
    );
    updateElement({ ...element, content: { ...content, options: newOptions } });
    postAPI('/remove_option', { id: element.id, index }, () => {})
  };

  return (
    <>
      <input
        type="text"
        value={content.question}
        onChange={updateQuestion}
        placeholder="Nom del pagament"
        className={(errors.question && !errors.question.isWarning ? 'error' : '') + ' question'}
      />
      {errors.question && (
        <p className={errors.question.isWarning ? "warning-text" : "error-text"}>
          {errors.question.message || errors.question}
        </p>
      )}
      <p style={{ fontSize: 11, color: '#777' }}>Quan la gent respongui una opció, s'afegiran a la llista de "demanats" per aquest pagament.</p>
      <div className="options-container">
        {content.options
        .map((option, index) => !option.hidden && (
          <div key={index} className="option-container">
            {errors[`option_${index}`] && (
              <p className={errors[`option_${index}`].isWarning ? "warning-text" : "error-text"} style={{ width: 100 }}>
                {errors[`option_${index}`].message || errors[`option_${index}`]}
              </p>
            )}
            <input
              type="text"
              value={option.text}
              onChange={(e) => updateOption(index, 'text', e.target.value)}
              placeholder={`Opció ${index + 1}`}
              className="option-input"
            />
            <input
              type="text"
              inputMode="decimal"
              value={decimalValueToInput(option.price)}
              onChange={(e) => updateOption(index, 'price', sanitizeDecimalInput(e.target.value))}
              placeholder="Preu"
              className="option-input"
              style={{ width: 55, flexGrow: 0, marginLeft: 10 }}
            />
            €
            <input
              type="number"
              value={option.limit ?? ''}
              onWheel={(event) => event.currentTarget.blur()}
              onChange={(e) => updateOption(index, 'limit', sanitizeLimitInput(e.target.value))}
              placeholder="Límit"
              className="option-input"
              style={{ width: 55, flexGrow: 0, marginLeft: 10 }}
              min={0}
            />
            <button onClick={() => hideOption(index)} style={{ backgroundColor: 'rgb(255, 200, 200)', color: 'white', marginLeft: 10 }}>🗑️</button>
          </div>
        ))}
        <button onClick={addOption} style={{ marginTop: 10 }}>+ Afegir opció</button>
      </div>
    </>
  );
};

const ResumPagamentsQuestion = ({ element, updateElement, removeElement, errors }) => {
  const { content } = element;

  return (
    <>
      <h2>Resum de pagaments</h2>
      <p style={{ fontSize: 14 }}>Es farà la suma de tots els pagaments demanats per l'usuari.</p>

    </>
  );
};

const StripePaymentQuestion = ({ element, updateElement, removeElement, errors }) => {
  const { content } = element;

  const updateButtonText = (e) => {
    updateElement({ ...element, content: { ...content, buttonText: e.target.value } });
  };

  const updateCommissionMode = (e) => {
    updateElement({ ...element, content: { ...content, commissionMode: e.target.value } });
  };

  // Commission calculation for preview
  const { percent: TOTAL_PERCENT, fixed: TOTAL_FIXED, description: commissionDescription } = getStripeCommissionInfoSync();
  const exampleAmount = 10;
  const exampleCommission = Math.round((exampleAmount * TOTAL_PERCENT + TOTAL_FIXED) * 100) / 100;

  return (
    <>
      <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        Pagament amb targeta
      </h2>

      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
        Text del botó:
      </label>
      <input
        type="text"
        value={content?.buttonText || 'Pagar amb targeta'}
        onChange={updateButtonText}
        placeholder="Pagar amb targeta"
        className="question"
        style={{ marginBottom: 15 }}
      />

      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
        Qui paga la comissió?
      </label>
      <select
        value={content?.commissionMode || 'user_pays'}
        onChange={updateCommissionMode}
        style={{ padding: '10px', fontSize: '14px', width: '100%', marginBottom: 15 }}
      >
        <option value="user_pays">L'usuari paga la comissió (la colla rep l'import complet)</option>
        <option value="colla_absorbs">La colla absorbeix la comissió (l'usuari paga l'import exacte)</option>
      </select>

      <div style={{ backgroundColor: '#f0f7ff', padding: 15, borderRadius: 8, marginBottom: 15, border: '1px solid #cce0ff' }}>
        <p style={{ fontSize: 13, margin: 0, color: '#555' }}>
          <strong>Comissió:</strong> {commissionDescription} per transacció
        </p>
        <p style={{ fontSize: 12, margin: '5px 0 0 0', color: '#777' }}>
          Exemple: Per {exampleAmount}€, la comissió és {formatMoney(exampleCommission)}€
        </p>
      </div>

    </>
  );
};

const CopiableTextQuestion = ({ element, updateElement, removeElement, errors }) => {
  const { content } = element;

  const updateQuestion = (e) => {
    updateElement({ ...element, content: { ...content, question: e.target.value } });
  };

  const updateText = (e) => {
    updateElement({ ...element, content: { ...content, text: e.target.value } });
  };

  return (
    <>
      <input
        type="text"
        value={content.question || ''}
        onChange={updateQuestion}
        placeholder="Títol de la pregunta"
        className={(errors.question && !errors.question.isWarning ? 'error' : '') + ' question'}
      />
      {errors.question && (
        <p className={errors.question.isWarning ? "warning-text" : "error-text"}>
          {errors.question.message || errors.question}
        </p>
      )}
      <input
        type="text"
        value={content.text || ''}
        onChange={updateText}
        placeholder="Text per copiar"
        className={(errors.text && !errors.text.isWarning ? 'error' : '') + ' question'}
        style={{ marginTop: 5, backgroundColor: 'rgba(0,0,255,0.1)', color: '#555' }}
      />
      {errors.text && (
        <p className={errors.text.isWarning ? "warning-text" : "error-text"}>
          {errors.text.message || errors.text}
        </p>
      )}
      <p style={{ fontSize: 10 }}>Pots utilitzar: <span style={{ color: 'darkblue' }}>{['{nom}', '{cognom}', '{sobrenom}'].join(', ')}</span> als textos per copiar.</p>
    </>
  );
};

export {
    MultipleChoiceQuestion,
    CheckboxQuestion,
    ShortAnswerQuestion,
    ParagraphQuestion,
    ImageUploadQuestion,
    TextElement,
    CopiableTextQuestion,
    AssistanceQuestion,
    TicketQuestion,
    TicketV2Question,
    ImageElement,
    ResumPagamentsQuestion,
    TicketV2SingleQuestion,
    StripePaymentQuestion,
};

export const MultipleChoiceQuestionPreview = ({ element }) => {
  return (
    <div>
      <h3>{element.content.question}</h3>
      {element.content.options.map((option, index) => (
        <div key={index}>
          <input type="radio" id={`${element.id}-${index}`} name={element.id} disabled />
          <label htmlFor={`${element.id}-${index}`}>{option}</label>
        </div>
      ))}
    </div>
  );
};

export const CheckboxQuestionPreview = ({ element }) => {
  return (
    <div>
      <h3>{element.content.question}</h3>
      {element.content.options.map((option, index) => (
        <div key={index}>
          <input type="checkbox" id={`${element.id}-${index}`} name={element.id} disabled />
          <label htmlFor={`${element.id}-${index}`}>{option}</label>
        </div>
      ))}
    </div>
  );
};

export const AssistanceQuestionPreview = ({ element }) => {
  return (
    <div>
      <h3>{element.content.question}</h3>
      <div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="radio" disabled />
          <span>{element.content.yesLabel || 'Sí'}</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="radio" disabled />
          <span>{element.content.noLabel || 'No'}</span>
        </label>
      </div>
    </div>
  );
};

export const ShortAnswerQuestionPreview = ({ element }) => {
  return (
    <div>
      <h3>{element.content.question}</h3>
      <input type="text" readOnly placeholder={'Resposta de curta durada'} />
    </div>
  );
};

export const ParagraphQuestionPreview = ({ element }) => {
  return (
    <div>
      <h3>{element.content.question}</h3>
      <textarea style={{ fontSize: 13 }} readOnly placeholder={'Resposta de llarga durada'} />
    </div>
  );
};

export const TextElementPreview = ({ element }) => {
  return <h4 style={{ fontWeight: 'normal' }}>
    {element.content}
  </h4>;
};

export const ImageUploadQuestionPreview = ({ element }) => {
  return (
    <div>
      <h3>{element.content.question}</h3>
      <input type="file" disabled />
    </div>
  );
};

export const ResumPagamentsQuestionPreview = ({ element }) => {
  return (
    <div>
      <h3>Resum de pagaments</h3>
      <p>Es farà la suma dels pagaments demanats.</p>
    </div>
  );
};

export const StripePaymentQuestionPreview = ({ element }) => {
  const { content } = element;
  const buttonText = content?.buttonText || 'Pagar amb targeta';

  return (
    <div>
      <h3>{buttonText}</h3>
      <p style={{ color: '#666', margin: 0 }}>Apple Pay i targeta disponibles</p>
    </div>
  );
}

export const CopiableTextQuestionPreview = ({ element }) => {
  const copyToClipboard = async (text) => {
    // Copy to clipboard logic
  };

  return (
    <div>
      <h3>{element.content.question}</h3>
      <input type="text" readOnly value={element.content.text} style={{ backgroundColor: 'rgba(0,0,255,0.1)', color: '#555' }} />
    </div>
  );
};

export const TicketQuestionPreview = ({ element }) => {
  return (
    <div>
      <h3>{element.content.question}</h3>
      {element.content.options.map((option, index) => (
        <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5em' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <input type="checkbox" checked={index === 0} disabled style={{ marginRight: '0.5em' }} />
            <span>{option}</span>
          </div>
          {index === 0 && (
            <div>
              {element.limit !== null && element.limit !== undefined && (
                <em style={{ marginRight: '10px', fontSize: '0.9em', color: '#666' }}>(Límit: {element.limit})</em>
              )}
              {element.price !== '' && !isNaN(element.price) && <strong>{formatMoney(element.price)}€</strong>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export const ImagePreview = ({ selectedEvent, element }) => {
  const [imageURL, setImageURL] = useState(null);

  useEffect(() => {
    fetchAPI(`/form_image_url/${selectedEvent}/${element.id}`, ({ elementId, url }) => {
      fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error('Error fetching image from S3');
          return res.text();
        })
        .then((data) => {
          setImageURL(data);
        })
        .catch((err) => {
          // Not found
        })
    }, false, false)
  }, [
    selectedEvent,
    element.id,
  ])

  return (
    <div>
      <img
        src={imageURL}
        style={{ maxWidth: '100%' }}
      />
    </div>
  );
}

export const TicketV2QuestionPreview = ({ element }) => {
  return (
    <div>
      <h3>{element.content.question}</h3>
      {element.content.options.map((option, index) => !option.hidden && (
        <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <input type="radio" name={`ticket-${element.id}`} id={`ticket-${element.id}-${index}`} disabled />
          <label htmlFor={`ticket-${element.id}-${index}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span>{option.text}</span>
            <span>
              {option.limit !== null && option.limit !== undefined && <em style={{ marginRight: '10px', fontSize: '0.9em', color: '#666' }}>(Límit: {option.limit})</em>}
              <strong>{formatMoney(option.price)}€</strong>
            </span>
          </label>
        </div>
      ))}
    </div>
  );
};

export const TicketV2SingleQuestionPreview = ({ element }) => {
  return (
    <div>
      <h3>{element.content.question}</h3>
      {element.content.options.map((option, index) => !option.hidden && (
        <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <input type="radio" name={`ticket-${element.id}`} id={`ticket-${element.id}-${index}`} disabled />
          <label htmlFor={`ticket-${element.id}-${index}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span>{option.text}</span>
            <span>
              {option.limit !== null && option.limit !== undefined && <em style={{ marginRight: '10px', fontSize: '0.9em', color: '#666' }}>(Límit: {option.limit})</em>}
              <strong>{formatMoney(option.price)}€</strong>
            </span>
          </label>
        </div>
      ))}
    </div>
  );
};